import { AddRegular, DeleteRegular, EditRegular } from "@fluentui/react-icons";
import {
  adminCreateProjectIqNotification,
  adminDeleteProjectIqNotification,
  adminGetProjectIqNotifications,
  adminUpdateProjectIqNotification,
} from "apps/project-iq/api";
import { ANNOUNCEMENT_TYPES, IAnnouncement } from "models/IAnnouncement";
import { EditAnnouncement } from "components/manageAnnouncements/editDialog/EditAnnouncement";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import {
  IAdminListAction,
  IAdminRowAction,
} from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { ITableColumn } from "models/ITableColumn";
import { useEffect, useMemo, useState } from "react";
import { formatDate } from "utils/generalUtils";
import { useManageIqNotificationsStyles } from "./ManageIqNotifications.styles";
import { INotificationDialogState } from "./ManageIqNotifications.types";

export const ManageIqNotifications = (): JSX.Element => {
  const colStyles = useAdminColumnStyles();
  const notifStyles = useManageIqNotificationsStyles();

  const [notifications, setNotifications] = useState<IAnnouncement[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogState, setDialogState] = useState<INotificationDialogState>({
    open: false,
    notification: null,
  });
  const [deleteTarget, setDeleteTarget] = useState<IAnnouncement | null>(null);

  const loadNotifications = async (silent = false): Promise<void> => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await adminGetProjectIqNotifications();
      setNotifications(data as IAnnouncement[]);
    } catch {
      setError("Failed to load notifications. Please try again.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    void loadNotifications();
  }, []);

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    try {
      await adminDeleteProjectIqNotification(deleteTarget.id!);
      setDeleteTarget(null);
      void loadNotifications();
    } catch {
      setError("Failed to delete notification. Please try again.");
      setDeleteTarget(null);
    }
  };

  const handleSave = (): void => {
    setDialogState({ open: false, notification: null });
    void loadNotifications();
  };

  const handleDismiss = (): void => {
    setDialogState({ open: false, notification: null });
  };

  const searchFields = useMemo(
    () => ["title", "displayName", "type"] as (keyof IAnnouncement)[],
    [],
  );

  const { searchQuery, handleSearchChange, filteredItems } =
    useAdminListSearch<IAnnouncement>({
      items: notifications,
      searchFields,
    });

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<IAnnouncement>({
    cacheKey: "IqNotifications",
    defaultSortColumn: "createdAt",
    defaultDescending: true,
  });

  const columns: ITableColumn<IAnnouncement>[] = useMemo(
    () => [
      {
        columnId: "title",
        displayValue: "Title",
        className: colStyles.wide,
        renderHeaderCell: () => "Title",
        compare: (a, b) => a.title.localeCompare(b.title),
        renderCell: (item) => (
          <span className={notifStyles.titleCell}>{item.title}</span>
        ),
      },
      {
        columnId: "type",
        displayValue: "Type",
        className: colStyles.normal,
        renderHeaderCell: () => "Type",
        compare: (a, b) => (a.type ?? "").localeCompare(b.type ?? ""),
        renderCell: (item) => (
          <span
            className={
              item.type === ANNOUNCEMENT_TYPES.WARNING
                ? notifStyles.pillWarning
                : notifStyles.pillInformational
            }
          >
            {item.type === ANNOUNCEMENT_TYPES.WARNING
              ? "Warning"
              : "Informational"}
          </span>
        ),
      },
      {
        columnId: "enabled",
        displayValue: "Enabled",
        className: colStyles.normal,
        renderHeaderCell: () => "Enabled",
        compare: (a, b) =>
          Number(a.enabled ?? false) - Number(b.enabled ?? false),
        renderCell: (item) => (
          <span
            className={
              item.enabled ? notifStyles.cellEnabled : notifStyles.cellDisabled
            }
          >
            {item.enabled ? "Enabled" : "Disabled"}
          </span>
        ),
      },
      {
        columnId: "displayName",
        displayValue: "Author",
        className: colStyles.normal,
        renderHeaderCell: () => "Author",
        compare: (a, b) =>
          (a.displayName ?? "").localeCompare(b.displayName ?? ""),
        renderCell: (item) => (
          <span className={notifStyles.authorCell}>{item.displayName}</span>
        ),
      },
      {
        columnId: "updatedAt",
        displayValue: "Last Updated",
        className: colStyles.normal,
        renderHeaderCell: () => "Last Updated",
        compare: (a, b) => a.updatedAt - b.updatedAt,
        renderCell: (item) => <span>{formatDate(item.updatedAt)}</span>,
      },
    ],
    [colStyles, notifStyles],
  );

  const sortedItems = useMemo(
    () => sortItems(filteredItems, columns),
    [filteredItems, sortItems, columns],
  );

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<IAnnouncement>({
    items: sortedItems,
    itemsPerPage: 15,
  });

  const rowActions: IAdminRowAction<IAnnouncement>[] = useMemo(
    () => [
      {
        icon: <EditRegular />,
        tooltip: "Edit",
        onClick: (item) => setDialogState({ open: true, notification: item }),
        disabled: () => false,
      },
      {
        icon: <DeleteRegular />,
        tooltip: "Delete",
        onClick: (item) => setDeleteTarget(item),
        disabled: () => false,
      },
    ],
    [],
  );

  const headerActions: IAdminListAction[] = useMemo(
    () => [
      {
        label: "Add New",
        icon: <AddRegular />,
        appearance: "primary",
        onClick: () => setDialogState({ open: true, notification: null }),
      },
    ],
    [],
  );

  return (
    <>
      <AdminListPage<IAnnouncement>
        title="Notifications"
        items={notifications}
        columns={columns}
        isLoading={loading}
        headerActions={headerActions}
        searchPlaceholder="Search notifications..."
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        rowActions={rowActions}
        getRowId={(item) => item.id ?? ""}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={
          error ??
          (searchQuery
            ? "No notifications match the search."
            : "No notifications found.")
        }
        actionsWidth="100px"
      />
      <EditAnnouncement
        isOpen={dialogState.open}
        announcement={dialogState.notification}
        entityLabel="Notification"
        createFn={(data) => adminCreateProjectIqNotification(data)}
        updateFn={(data) =>
          adminUpdateProjectIqNotification(data.id!, {
            title: data.title,
            content: data.content,
            type: data.type,
            enabled: data.enabled,
          })
        }
        onSaved={handleSave}
        onDismiss={(saved) => {
          if (!saved) handleDismiss();
        }}
      />
      <ActionDialog
        isVisible={deleteTarget !== null}
        title="Delete Notification"
        message={`Are you sure you want to delete "${deleteTarget?.title}"? This cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={() => setDeleteTarget(null)}
      />
    </>
  );
};
